import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { api } from "../../api";
import { customStyles } from "../../styles/dataTableStyles";

const ActivityLogs = () => {
  const token = useSelector((state) => state.auth.token);

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchActivityLogs = useCallback(
    async (pageNo, searchKey, pageSize, startDate, endDate) => {
      try {
        setLoading(true);
        const response = await api.logs.getActivityLogList(
          {
            pageNo,
            pageSize,
            sort: "desc",
            sortBy: "id",
            searchKey,
            startDate,
            endDate,
          },
          token
        );

        if (response.success) {
          setLogs(response.data.result);
          setTotalRows(response.data.totalCount);
        } else {
          setLogs([]);
          setTotalRows(0);
        }
      } catch (error) {
        console.error("Error fetching activity logs", error);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Reset page on search change
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Fetch logs when page/search/pageSize/dates change
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchActivityLogs(page, search, pageSize, startDate, endDate);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [page, search, pageSize, startDate, endDate, fetchActivityLogs]);

  // Manual fetch for Get Logs button
  const handleClick = () => {
    setPage(1);
    fetchActivityLogs(1, search, pageSize, startDate, endDate);
  };

  const columns = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "80px" },
    { name: "User ID", selector: (row) => row.userId, sortable: true, width: "100px" },
    {
      name: "User Name",
      selector: (row) => row.user?.userName || row.userName,
      sortable: true,
      wrap: true,
    },
    { name: "Order ID", selector: (row) => row.orderId, sortable: true,wrap: true, width: "120px" },
    { name: "Type", selector: (row) => row.type, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true },
    { name: "IP Address", selector: (row) => row.ipAddress,wrap: true,sortable: true },
    {
      name: "Activity Time",
      selector: (row) => row.activityTime,
      sortable: true,
      cell: (row) =>
        row.activityTime ? <span>{new Date(row.activityTime).toLocaleString()}</span> : "-",
    },
    {
      name: "Payload",
      selector: (row) => row.payload,
      wrap: true,
      cell: (row) =>
        row.payload ? (
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "300px" }}>
            {JSON.stringify(row.payload, null, 2)}
          </pre>
        ) : (
          "-"
        ),
    },
    { name: "Error Message", selector: (row) => row.errorMessage, wrap: true },
  ];

  return (
    <div className="container-fluid mt-4">
      {/* Page Title Row */}
      <div className="d-flex align-items-center mb-3">
        <h2 className="sec-header">ACTIVITY LOGS</h2>
      </div>

      {/* Action Row: Search + Date Filters */}
      <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">
        {/* Search field */}
        <div className="d-flex align-items-center">
          <label className="mr-2">Search:</label>
          <input
            type="text"
            placeholder="Search logs"
            className="form-control"
            style={{ width: "200px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleClick()}
          />
        </div>

        {/* Date filters */}
        <div className="d-flex align-items-center">
          <label className="mr-2">Start Date:</label>
          <input
            type="date"
            value={startDate}
            className="form-control"
            style={{ width: "200px", marginRight: "10px" }}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <label className="mr-2">End Date:</label>
          <input
            type="date"
            value={endDate}
            className="form-control"
            style={{ width: "200px", marginRight: "10px" }}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={logs}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={setPage}
        paginationPerPage={pageSize}
        onChangeRowsPerPage={(newPageSize, newPage) => {
          setPageSize(newPageSize);
          setPage(newPage);
        }}
        paginationRowsPerPageOptions={[50, 100, 150, 200]}
        highlightOnHover
        customStyles={customStyles}
        responsive
        noHeader={false}
      />
    </div>
  );
};

export default ActivityLogs;
